import { config } from '../config';
import { authService } from './auth';

export interface CoinCheckResult {
  cost: number;
  hasEnoughCoins: boolean;
  currentBalance: number;
  shortfall: number;
}

export interface CoinTransaction {
  id: string;
  transaction_type: 'add' | 'spend' | 'set' | 'zero';
  amount: number;
  previous_balance: number;
  new_balance: number;
  reason?: string;
  metadata?: any;
  created_at: string;
}

class CoinService {
  private baseUrl = config.apiUrl;

  /**
   * Check if user has enough coins for TTS generation
   */
  async checkTTSCost(characterCount: number): Promise<CoinCheckResult> {
    try {
      const token = await authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseUrl}/check-tts-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ characterCount })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking TTS cost:', error);
      throw error;
    }
  }

  /**
   * Check if user has enough coins for batch TTS generation
   */
  async checkBatchTTSCost(segments: Array<{ text: string }>): Promise<CoinCheckResult> {
    const totalCharacters = segments.reduce((total, segment) => total + segment.text.length, 0);
    return this.checkTTSCost(totalCharacters);
  }

  /**
   * Get user's coin transaction history
   */
  async getCoinTransactions(limit: number = 50, offset: number = 0): Promise<CoinTransaction[]> {
    try {
      const token = await authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseUrl}/user/coin-transactions?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.transactions;
    } catch (error) {
      console.error('Error getting coin transactions:', error);
      throw error;
    }
  }

  /**
   * Format coin amount for display
   */
  formatCoinAmount(amount: number): string {
    return amount.toLocaleString();
  }

  /**
   * Calculate TTS cost for text
   */
  calculateTTSCost(text: string): number {
    return text.length;
  }

  /**
   * Check if error is a coin-related error
   */
  isCoinError(error: any): boolean {
    if (typeof error === 'string') {
      return error.toLowerCase().includes('insufficient coins') || 
             error.toLowerCase().includes('coin');
    }
    if (error instanceof Error) {
      return error.message.toLowerCase().includes('insufficient coins') || 
             error.message.toLowerCase().includes('coin');
    }
    return false;
  }

  /**
   * Reuse cached speech for a segment
   */
  async reuseCachedSpeech(params: {
    segmentId: string;
    dbSegmentId?: string;
    scriptId?: string;
    partId?: string;
    text: string;
    voiceId: string;
    language?: string;
    speed?: number;
    inputAudioFile?: string;
  }): Promise<{
    success: boolean;
    cached: boolean;
    segmentId: string;
    audioFile: string;
    duration?: number;
    cacheId: string;
    message: string;
  }> {
    try {
      const token = await authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseUrl}/reuse-cached-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error reusing cached speech:', error);
      throw error;
    }
  }

  /**
   * Extract coin information from error message
   */
  parseCoinError(error: any): { required?: number; available?: number; shortfall?: number } | null {
    let message = '';
    if (typeof error === 'string') {
      message = error;
    } else if (error instanceof Error) {
      message = error.message;
    } else {
      return null;
    }

    const requiredMatch = message.match(/Required:\s*(\d+)/i);
    const availableMatch = message.match(/Available:\s*(\d+)/i);
    
    if (requiredMatch && availableMatch) {
      const required = parseInt(requiredMatch[1]);
      const available = parseInt(availableMatch[1]);
      return {
        required,
        available,
        shortfall: required - available
      };
    }

    return null;
  }
}

export const coinService = new CoinService();